import { useMemo, useRef, useState } from "react";
import { Link } from "wouter";
import { AlertTriangle, ArrowRight, BookmarkPlus, Loader2 } from "lucide-react";
import type { CalculationDraft } from "@shared/calculation-template";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { buildCalculationTemplateFromOffer } from "@/lib/offer-calculation-template";
import { useToast } from "@/hooks/use-toast";
import type { LVSektion } from "./leistungsverzeichnis-kalkulator";

export interface SaveOfferCalculationTemplateProps {
  sections: LVSektion[];
  projectName?: string;
}

const positionHasCalculation = (position: CalculationDraft["positions"][number]) =>
  position.k7Price !== null || position.manualCalculation === true;
const euro = new Intl.NumberFormat("de-AT", { style: "currency", currency: "EUR" });

export default function SaveOfferCalculationTemplate({
  sections,
  projectName,
}: SaveOfferCalculationTemplateProps) {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [acknowledged, setAcknowledged] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const submittingRef = useRef(false);
  const activeCount = useMemo(
    () => sections.reduce((count, section) => count + section.positionen.filter((position) => position.aktiv && !position.variant).length, 0),
    [sections],
  );
  const preview = useMemo(() => {
    if (!open || activeCount === 0) return { draft: null, error: "" };
    try {
      return { draft: buildCalculationTemplateFromOffer(name.trim() || "Kalkulation", sections), error: "" };
    } catch (cause) {
      return {
        draft: null,
        error: cause instanceof Error ? cause.message : "Das Angebot kann nicht als Vorlage vorbereitet werden.",
      };
    }
  }, [activeCount, name, open, sections]);
  const draft = preview.draft;
  const calculationCount = draft?.positions.filter(positionHasCalculation).length ?? 0;
  const priceOnlyCount = (draft?.positions.length ?? 0) - calculationCount;
  const fixedPriceWarnings = draft?.positions
    .filter((position) => typeof position.priceOverride === "number")
    .map((position) => `Fixierter Angebotspreis für ${position.code}: ${euro.format(position.priceOverride!)} / ${position.unit}. Änderungen an Ansätzen ändern diesen EP erst nach Aufheben der Preisfixierung.`) ?? [];
  const warnings = [...(draft?.warnings ?? []), ...fixedPriceWarnings];
  const hasWarnings = warnings.length > 0;
  const cleanName = name.trim().slice(0, 150);

  const openDialog = () => {
    setName(`${projectName?.trim() || "Bauvorhaben"} – Kalkulation`.slice(0, 150));
    setAcknowledged(false);
    setError("");
    setOpen(true);
  };

  const save = async () => {
    if (submittingRef.current || !draft || !cleanName) return;
    if (hasWarnings && !acknowledged) return;
    submittingRef.current = true;
    setSubmitting(true);
    setError("");
    try {
      const response = await apiRequest("POST", "/api/calculation-templates/from-offer", {
        draft: { ...draft, name: cleanName },
      });
      await response.json().catch(() => undefined);
      await queryClient.invalidateQueries({ queryKey: ["/api/calculation-templates"] });
      setOpen(false);
      toast({
        title: "Kalkulationsvorlage gespeichert",
        description: "Eine neue private Vorlage wurde angelegt. Das Angebot bleibt unverändert.",
        action: <Link href="/calculation-templates" className="inline-flex items-center gap-1">Vorlagen öffnen <ArrowRight className="h-3.5 w-3.5" /></Link>,
      });
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Die Vorlage konnte nicht gespeichert werden.");
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  return <>
    <Button type="button" variant="outline" onClick={openDialog} disabled={activeCount === 0}>
      <BookmarkPlus className="mr-2 h-4 w-4" />
      Als Kalkulationsvorlage speichern
    </Button>
    <Dialog open={open} onOpenChange={(next) => { if (!submitting) setOpen(next); }}>
      <DialogContent className="max-h-[90dvh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Neue Kalkulationsvorlage</DialogTitle>
          <DialogDescription>
            Aus dem aktuellen Angebotsstand wird eine neue private Vorlage erstellt. Eine bestehende Vorlage oder das Angebot wird nicht überschrieben.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="offer-calculation-template-name">Vorlagenname</Label>
            <Input
              id="offer-calculation-template-name"
              value={name}
              maxLength={150}
              disabled={submitting}
              onChange={(event) => setName(event.target.value.slice(0, 150))}
              autoFocus
            />
          </div>

          <div className="grid grid-cols-2 gap-3 rounded-lg border border-border/80 bg-muted/25 p-3">
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Mit Kalkulation</p>
              <p className="mt-1 font-mono text-xl font-semibold tabular-nums">{calculationCount}</p>
            </div>
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Nur Einheitspreis</p>
              <p className="mt-1 font-mono text-xl font-semibold tabular-nums">{priceOnlyCount}</p>
            </div>
            <p className="col-span-2 text-xs leading-relaxed text-muted-foreground">
              Alle aktiven Angebotspositionen werden übernommen. Ohne vorhandene Kalkulation bleibt der aktuelle LV-Preis als Preis-Fallback erhalten; es werden keine K7-Ansätze ergänzt.
            </p>
          </div>

          {hasWarnings && <Alert className="border-amber-500/40 bg-amber-50/70 text-amber-950">
            <AlertTriangle className="h-4 w-4 text-amber-700" />
            <AlertTitle>Bitte Hinweise prüfen</AlertTitle>
            <AlertDescription>
              <ul className="mt-1 max-h-32 list-disc space-y-1 overflow-y-auto pl-5">
                {warnings.map((warning, index) => <li key={`${index}-${warning}`}>{warning}</li>)}
              </ul>
            </AlertDescription>
          </Alert>}

          {(error || preview.error) && <Alert variant="destructive"><AlertTriangle className="h-4 w-4" /><AlertDescription>{error || preview.error}</AlertDescription></Alert>}

          {hasWarnings && <label className="flex cursor-pointer items-start gap-2 rounded-md border border-amber-500/30 p-3 text-sm leading-relaxed">
            <Checkbox checked={acknowledged} disabled={submitting} onCheckedChange={(checked) => setAcknowledged(checked === true)} />
            <span>Ich habe die Hinweise geprüft und möchte diese neue Vorlage trotzdem anlegen.</span>
          </label>}
        </div>

        <DialogFooter className="gap-2">
          <Button type="button" variant="outline" disabled={submitting} onClick={() => setOpen(false)}>Abbrechen</Button>
          <Button type="button" disabled={submitting || !draft || !cleanName || (hasWarnings && !acknowledged)} onClick={save}>
            {submitting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Wird gespeichert …</> : "Neue Vorlage anlegen"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </>;
}
